import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Button from '@material-ui/core/Button';
import clsx from 'clsx';
import moment from 'moment';
import { FormField, Pagination } from '@iris-platform/iris-react-ui';
import { map } from 'ramda';
import Skeleton from 'react-loading-skeleton';
import UserDisableModal from './UserDisableModal';
import UserDeleteModal from './UserDeleteModal';

const styles = theme => ({
  root: {
    width: '100%',
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  headContent: {
    borderBottom: 'none',
  },
  formHeading: {},
  column: {
    flexBasis: '33.33%',
  },
  rightColumn: {
    flexBasis: '66.66%',
  },
  helper: {
    textAlign: 'right',
    borderLeft: `2px solid ${theme.palette.divider}`,
    padding: '0px 10px',
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  textField: {
    marginLeft: theme.spacing.unit,
    marginRight: theme.spacing.unit,
  },
  button: {
    margin: theme.spacing.unit,
  },
  disabledUserInfo: {
    textAlign: 'left',
  },
});

/**
 * @typedef {Object} Props
 * @property {(user_id: string, disabled: boolean, reason: string) => void} onUserDisable
 * @property {(user_id: string) => void} onUserDelete
 */
/**
 * @typedef {Object} State
 * @property {string | null} expanded
 * @property {'disable' | 'delete' | null} modal
 */

/** @extends React.Component<Props, State> */
class UserList extends Component {
  /** @type {State} */
  state = {
    expanded: null,
    modal: null,
  };

  handleChange = panel => (event, expanded) => {
    this.setState({
      expanded: expanded ? panel : false,
      modal: null,
    });
  };

  /**
   * Open 'Disable User' modal
   */
  requestDisableUser = () => this.setState({ modal: 'disable'});

  /**
   * Open 'Delete User' modal
   */
  requestDeleteUser = () => this.setState({ modal: 'delete'});

  /**
   * @param {string} user_id
   * @param {string} [reason]
  */
  onDisableUser = (user_id, reason = '') => {
    const { onUserDisable } = this.props;

    onUserDisable(user_id, true, reason);
    // Close modal
    this.setState(({ expanded, modal }) => (expanded == user_id && modal == 'disable') ? { modal: null } : null);
    //TODO: waiting icon
  }

  /**
   * @param {string} user_id
   */
  onEnableUser = (user_id) => () => {
    const { onUserDisable } = this.props;

    onUserDisable(user_id, false, '');
  }

  /**
   * @param {string} user_id
   */
  onDeleteUser = (user_id) => {
    const { onUserDelete } = this.props;

    onUserDelete(user_id);

    // Close modal
    this.setState(({ expanded, modal }) => (expanded == user_id && modal == 'delete') ? { modal: null } : null);
    //TODO: waiting icon
  }

  onModalCancel = () => this.setState({ modal: null });

  onDisableUserMessageChange = e => this.setState({ disabledMessage: e.target.value });

  renderDisabledInfo = (user, classes) => (
    <div>
      {user.auminfo.disabled && (
        <div className={classes.disabledUserInfo}>
          <Typography>Disabled On</Typography>
          <Typography variant="caption">
            {moment(user.auminfo.disabledTime).format('MMMM Do YYYY, h:mm:ss a')}
          </Typography>
          <br />
          <Typography>Reason</Typography>
          <Typography variant="caption">{user.auminfo.disabledDesc}</Typography>
        </div>
      )}
    </div>
  );

  renderUserList = (users, classes) => {
    const { expanded, modal } = this.state;
    return (
      <TableBody>
        {map(
          user => (
            <TableRow key={user.user_id}>
              <TableCell className={classes.headContent}>
                <ExpansionPanel
                  expanded={expanded === user.user_id}
                  onChange={this.handleChange(user.user_id)}
                >
                  <ExpansionPanelSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <Typography className={classes.heading}>{user.auminfo.user_name}</Typography>
                    <Typography className={classes.secondaryHeading}>{user.auminfo.email}</Typography>
                  </ExpansionPanelSummary>
                  <ExpansionPanelDetails>
                    <div className={classes.column}>
                      <Typography>Created</Typography>
                      <Typography variant="caption">
                        {moment(user.timestamp).format('MMMM Do YYYY, h:mm:ss a')}
                      </Typography>
                      <br />
                      <Typography>Last login</Typography>
                      <Typography variant="caption">
                        {moment.unix(user.auminfo.last_login_time).format('MMMM Do YYYY, h:mm:ss a')}
                      </Typography>
                    </div>
                    <div className={clsx(classes.rightColumn, classes.helper)}>
                      {this.renderDisabledInfo(user, classes)}
                      <Button
                        variant="contained"
                        className={classes.button}
                        size="small"
                        aria-haspopup="true"
                        onClick={user.auminfo.disabled
                          ? this.onEnableUser(user.user_id)
                          : this.requestDisableUser}
                      >
                        {user.auminfo.disabled ? 'Enable' : 'Disable'}
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        className={classes.button}
                        size="small"
                        aria-haspopup="true"
                        onClick={this.requestDeleteUser}
                      >
                        Delete
                      </Button>
                      <UserDisableModal
                        open={modal == 'disable'}
                        userId={user.user_id}
                        onSubmit={this.onDisableUser}
                        onCancel={this.onModalCancel}
                      />
                      <UserDeleteModal
                        open={modal == 'delete'}
                        userId={user.user_id}
                        userEmail={user.auminfo.email}
                        onSubmit={this.onDeleteUser}
                        onCancel={this.onModalCancel}
                      />
                    </div>
                  </ExpansionPanelDetails>
                </ExpansionPanel>
              </TableCell>
              {/* <TableCell className={classes.headContent}>{user.auminfo.user_name}</TableCell>
              <TableCell className={classes.headContent}>{user.auminfo.email}</TableCell> */}
            </TableRow>
          ),
          users,
        )}
      </TableBody>
    );
  };

  renderSkeleton = () => (
    <TableBody>
      {Array(5)
        .fill(null)
        .map(() => (
          <TableRow key={Math.random()}>
            <TableCell>
              <Skeleton />
            </TableCell>
            <TableCell>
              <Skeleton />
            </TableCell>
          </TableRow>
        ))}
    </TableBody>
  );

  render() {
    const {
      classes,
      users,
      isLoading,
      searchTxt,
      onClickPage,
      onChangeInput,
      recordsPerPage,
      currentPage,
      thresholdPageBtns,
      formPlaceholder,
    } = this.props;
    const totalRecords = users.length;
    const currentPageRecords = users.filter(
      (o, i) => i >= (currentPage - 1) * recordsPerPage && i < currentPage * recordsPerPage,
    );

    return (
      <div>
        <FormField
          className={classes.formHeading}
          placeholder={formPlaceholder}
          // label="Search based on current filter"
          value={searchTxt}
          name="searchTxt"
          onChange={onChangeInput}
        />
        <br />
        <Table className={classes.table}>
          {/* <TableHead>
            <TableRow className={classes.heading}>
              <TableCell className={classes.headContent}>UserName</TableCell>
              <TableCell className={classes.headContent}>Email</TableCell>
            </TableRow>
          </TableHead> */}
          {isLoading
            ? this.renderSkeleton()
            : this.renderUserList(currentPageRecords, classes)}
        </Table>
        <br />
        <div style={{ textAlign: 'center' }}>
          <Pagination
            title="usersPagination"
            totalRecords={totalRecords}
            recordsPerPage={recordsPerPage}
            currentPage={currentPage}
            thresholdPageBtns={thresholdPageBtns}
            onClickPage={onClickPage}
          />
        </div>
      </div>
    );
  }
}

UserList.propTypes = {
  users: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  formPlaceholder: PropTypes.string.isRequired,
  searchTxt: PropTypes.string.isRequired,
  thresholdPageBtns: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  recordsPerPage: PropTypes.number.isRequired,
  onClickPage: PropTypes.func.isRequired,
  onChangeInput: PropTypes.func.isRequired,
  onUserDisable: PropTypes.func.isRequired,
};

export default withStyles(styles)(UserList);
